import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

interface CaseReportBusinessesInvolvedProps {
    value: any[];
    isReadOnly?: boolean;
    canSaveNpsdData?: boolean;
    onChange?: (businesses: any[]) => void;
    onAddExisting?: (type: string) => void;
    onAddNew?: (type: string) => void;
    onAddUnknown?: (type: string) => void;
    onViewItem?: (type: string, id: any) => void;
    onRemoveItem?: (type: string, item: any) => void;
}

export function CaseReportBusinessesInvolved({
    value: businesses,
    isReadOnly = false,
    canSaveNpsdData = false,
    onChange,
    onAddExisting,
    onAddNew,
    onAddUnknown,
    onViewItem,
    onRemoveItem,
}: CaseReportBusinessesInvolvedProps) {
    const { theme } = useTheme();
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({ open: false, item: null });
    const prevLengthRef = useRef(businesses?.length || 0);
    const rootRef = useRef<HTMLDivElement>(null);

    // Watch businesses length for scroll-to-last
    useEffect(() => {
        const newLength = businesses?.length || 0;
        if (newLength > prevLengthRef.current) {
            setTimeout(() => {
                if (rootRef.current) {
                    scrollToLastMobileCard(rootRef.current);
                }
            }, 0);
        }
        prevLengthRef.current = newLength;
    }, [businesses?.length]);

    const confirmDelete = useCallback((business: any) => {
        setDeleteConfirm({ open: true, item: business });
    }, []);

    const doDelete = useCallback(() => {
        const item = deleteConfirm.item;
        if (!item) {
            setDeleteConfirm({ open: false, item: null });
            return;
        }
        onRemoveItem?.('Business', item);
        setDeleteConfirm({ open: false, item: null });
    }, [deleteConfirm.item, onRemoveItem]);

    const cancelDelete = useCallback(() => {
        setDeleteConfirm({ open: false, item: null });
    }, []);

    const formatBusinessName = useCallback((b: any) => {
        return b.businessName || b.name || 'Unknown Business';
    }, []);

    const formatAddress = useCallback((b: any) => {
        const parts = [b.address, b.city, b.state].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '';
    }, []);

    const formatPhone = useCallback((b: any) => {
        return b.phone || b.phoneNumber || '';
    }, []);

    return (
        <div className={'case-report-involved-grid' + (theme ? ' ' + theme : '')} ref={rootRef}>
            <div className="header">
                <h1>Business(es) <span className="hidden-sm hidden-xs">Involved</span></h1>
                <div className="header-buttons">
                    {canSaveNpsdData && !isReadOnly && (
                        <button
                            type="button"
                            className="btn-add btn-secondary hidden-sm hidden-xs"
                            onClick={() => onAddUnknown?.('Business')}
                        >
                            <i className="fas fa-plus"></i> UNKNOWN
                        </button>
                    )}
                    {canSaveNpsdData && !isReadOnly && (
                        <button
                            type="button"
                            className="btn-add btn-secondary"
                            onClick={() => onAddNew?.('Business')}
                        >
                            <i className="fas fa-plus"></i> NEW
                        </button>
                    )}
                    {!isReadOnly && (
                        <button
                            type="button"
                            className="btn-add"
                            onClick={() => onAddExisting?.('Business')}
                        >
                            <i className="fas fa-plus"></i> EXISTING
                        </button>
                    )}
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>Name</th>
                            <th style={{ width: '25%' }}>Branch Name</th>
                            <th style={{ width: '25%' }}>Address</th>
                            <th style={{ width: '12%' }}>Phone</th>
                            {!isReadOnly && <th style={{ width: '8%' }}>Actions</th>}
                        </tr>
                    </thead>
                    {businesses.length === 0 ? (
                        <tbody>
                            <tr>
                                <td colSpan={isReadOnly ? 4 : 5} className="empty-cell">
                                    No businesses involved
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {businesses.map((business: any) => (
                                <tr key={business.id}>
                                    <td>
                                        <a href="#" className="item-link" onClick={(e) => { e.preventDefault(); onViewItem?.('Business', business.id); }}>
                                            {formatBusinessName(business)}
                                        </a>
                                    </td>
                                    <td>{business.branchName || ''}</td>
                                    <td>{formatAddress(business)}</td>
                                    <td>{formatPhone(business)}</td>
                                    {!isReadOnly && (
                                        <td>
                                            <div className="actions">
                                                <button className="btn-delete" onClick={() => confirmDelete(business)} title="Remove business">
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="mobile-cards">
                {businesses.length === 0 ? (
                    <div className="empty-state">
                        <p>No businesses involved</p>
                    </div>
                ) : (
                    businesses.map((business: any) => (
                        <div key={'mobile-' + business.id} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    <a href="#" className="card-item-name" onClick={(e) => { e.preventDefault(); onViewItem?.('Business', business.id); }}>
                                        {formatBusinessName(business)}
                                    </a>
                                </div>
                                {!isReadOnly && <button className="btn-delete" onClick={() => confirmDelete(business)}>Remove</button>}
                            </div>

                            <div className="card-grid">
                                <div className="card-field">
                                    <div className="card-field-label">Branch</div>
                                    <div className="card-field-value">{business.branchName || '\u2014'}</div>
                                </div>
                                <div className="card-field">
                                    <div className="card-field-label">Phone</div>
                                    <div className="card-field-value">{formatPhone(business) || '\u2014'}</div>
                                </div>
                                <div className="card-field card-field-full">
                                    <div className="card-field-label">Address</div>
                                    <div className="card-field-value">{formatAddress(business) || '\u2014'}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                open={deleteConfirm.open}
                title="Remove Business?"
                messageHtml="Are you sure you want to remove this business from the report?"
                confirmText="Remove"
                cancelText="Cancel"
                confirmClass="danger"
                onConfirm={doDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

(window as any).CaseReportBusinessesInvolvedReact = CaseReportBusinessesInvolved;
